// FILE: src/UserAdmin.js
// UserAdmin.js - with bootstrap/self-promote first admin + slide-in Menu
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import Menu from "./Menu";
import "./styles.css";

// Match Menu drawer typography/colour (see Menu.js)
const MENU_FONT = "'Montserrat', 'Open Sans', Arial, sans-serif";
const MENU_HEADING_COLOR = "#e2eefd";
const MENU_HEADING_SHADOW = "2px 2px 14px #2ae, 0 2px 12px #09f, 0 4px 16px #222c";

const ROLES = ["Quiz Host", "Quiz Creator", "Quiz Master"];

function formatDate(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
}

function roleDisplay(role) {
  if (!role) return "";
  if (role === "Quiz Host") return "Quiz Host";
  if (role === "Quiz Creator") return "Quiz Creator";
  if (role === "Quiz Master") return "Quiz Master";
  return role;
}

const BUTTON_STYLE = {
  background: "#47e373",
  color: "#111",
  fontWeight: 700,
  border: "none",
  borderRadius: 10,
  padding: "0.7em 2.5em",
  fontSize: "1.08em",
  minWidth: 110,
  margin: "0 8px",
  cursor: "pointer",
};
const DANGER_BUTTON = {
  ...BUTTON_STYLE,
  background: "#ff4a4a",
  color: "#fff",
};

export default function UserAdmin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Quiz Host");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [bgUrl, setBgUrl] = useState("");
  const [showBootstrap, setShowBootstrap] = useState(false);

  // slide-in Menu
  const [showMenu, setShowMenu] = useState(false);

  // Check if running on localhost
  const isLocalhost = window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '::1';

  // Set the background
  useEffect(() => {
    setBgUrl("/media/Brainbackground.png");
  }, []);

  // Get logged in user + their role
  useEffect(() => {
    setLoading(true);
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      const snapshot = await getDocs(collection(db, "users"));
      const foundUsers = [];
      let foundRole = "";
      snapshot.forEach((docSnap) => {
        foundUsers.push({ uid: docSnap.id, ...docSnap.data() });
        if (firebaseUser && docSnap.id === firebaseUser.uid) {
          foundRole = docSnap.data().role;
        }
      });

      setUsers(foundUsers);
      setRole(foundRole);
      setLoading(false);
      setShowBootstrap(!!firebaseUser && foundUsers.length === 0);
    });
    return () => unsub();
  }, [success]);

  // Permission helpers
  const isMaster = role === "Quiz Master";
  const isCreator = role === "Quiz Creator";

  // Bootstrap self-promote logic
  async function handleBootstrap() {
    setError("");
    setSuccess("");
    try {
      if (!user) {
        setError("You must be signed in to bootstrap.");
        return;
      }
      await setDoc(doc(db, "users", user.uid), {
        email: user.email,
        displayName: user.displayName || user.email.split("@")[0],
        role: "Quiz Master",
        createdAt: Date.now(),
        lastLogin: Date.now(),
      });
      setSuccess("You are now the Quiz Master! Reloading...");
      setShowBootstrap(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      setError("Failed to create admin: " + err.message);
    }
  }

  // Invite a new user (Quiz Master only)
  async function handleInviteUser(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!inviteEmail || !inviteRole) {
      setError("Please enter email and role.");
      return;
    }
    try {
      if (!isMaster && inviteRole !== "Quiz Host") {
        setError("Only Quiz Masters can invite Creators or Masters.");
        return;
      }
      const allUsers = users;
      if (allUsers.find((u) => u.email === inviteEmail)) {
        setError("User already exists.");
        return;
      }
      const tempUid = "pending_" + Math.random().toString(36).substr(2, 8);
      await setDoc(doc(db, "users", tempUid), {
        email: inviteEmail,
        role: inviteRole,
        displayName: "",
        createdAt: Date.now(),
        lastLogin: "",
        status: "pending",
      });
      setSuccess("User invite created. You must now add them via Firebase Auth, then update their UID in Firestore.");
      setInviteEmail("");
      setInviteRole("Quiz Host");
    } catch (err) {
      setError("Error inviting user: " + err.message);
    }
  }

  // Role update
  async function handleRoleChange(uid, newRole) {
    setError("");
    setSuccess("");
    try {
      const target = users.find((u) => u.uid === uid);
      if (!target) return;
      if (!isMaster && (target.role !== "Quiz Host" || newRole !== "Quiz Host")) {
        setError("Only Quiz Masters can change roles for Creators or other Masters.");
        return;
      }
      await updateDoc(doc(db, "users", uid), { role: newRole });
      setSuccess("Role updated.");
    } catch (err) {
      setError("Error updating role: " + err.message);
    }
  }

  // Delete user (Firestore only)
  async function handleDelete(uid) {
    setError("");
    setSuccess("");
    if (
      !window.confirm(
        "Delete this user? This removes their user profile from Firestore only. You should also delete them in Firebase Auth."
      )
    )
      return;
    try {
      const target = users.find((u) => u.uid === uid);
      if (!target) return;
      if (!isMaster && target.role !== "Quiz Host") {
        setError("Only Quiz Masters can delete Creators or Masters.");
        return;
      }
      await deleteDoc(doc(db, "users", uid));
      setSuccess("User deleted from Firestore.");
    } catch (err) {
      setError("Error deleting user: " + err.message);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100vw",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        background: bgUrl ? `url(${bgUrl}) center center / cover no-repeat` : "#223b4c",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontFamily: "inherit",
      }}
    >
      {/* Top Bar with Menu Button (replaces old navbar) */}
      <div
        style={{
          width: "100vw",
          padding: "14px 16px",
          background: "rgba(20,15,44,0.55)",
          display: "flex",
          alignItems: "center",
          gap: 12,
          position: "sticky",
          top: 0,
          zIndex: 999,
        }}
      >
        <button
          aria-label="Open menu"
          title="Menu"
          onClick={() => setShowMenu(true)}
          style={{
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
            color: MENU_HEADING_COLOR,
            fontWeight: 900,
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
            fontFamily: MENU_FONT,
            textShadow: MENU_HEADING_SHADOW,
            letterSpacing: 0.3,
          }}
        >
          ☰ Menu
        </button>

        <div style={{ marginLeft: 8, fontWeight: 900, color: "#f8faff" }}>
          User & Admin
        </div>
      </div>

      {/* Slide-in Menu drawer */}
      <Menu open={showMenu} onClose={() => setShowMenu(false)} />

      {/* Bootstrap self-promote for first admin */}
      {showBootstrap && user && (
        <div
          style={{
            background: "rgba(32,30,48,0.85)",
            borderRadius: "1.3em",
            marginTop: "60px",
            padding: "2em 2.2em",
            textAlign: "center",
            boxShadow: "0 8px 36px 0 #111a",
            color: "#ffe066",
            fontWeight: 700,
            maxWidth: 450,
            fontSize: "1.22em",
          }}
        >
          <div style={{ marginBottom: 22 }}>
            No admin users found.<br />
            <span style={{ color: "#fff", fontWeight: 900 }}>Promote yourself to Quiz Master?</span>
          </div>
          <button style={BUTTON_STYLE} onClick={handleBootstrap}>
            Make Me Quiz Master
          </button>
        </div>
      )}

      {/* Main admin panel */}
      {!showBootstrap && (
        <div
          style={{
            background: "rgba(32,30,48,0.74)",
            marginTop: "40px",
            borderRadius: "1.5em",
            boxShadow: "0 8px 36px 0 #1119",
            padding: "2.4em 2em",
            minWidth: 350,
            width: "96vw",
            maxWidth: 750,
            color: "#fff",
            fontWeight: 600,
          }}
        >
          <h2
            style={{
              fontWeight: 900,
              color: "#ffe066",
              letterSpacing: 0.3,
              textShadow: "2px 2px 8px #222, 0 2px 12px #2ae",
              marginTop: 0,
            }}
          >
            User & Admin Management
          </h2>

          {/* Test Runner Link (Localhost only) */}
          {isLocalhost && (
            <div
              style={{
                marginBottom: "20px",
                padding: "12px 16px",
                background: "rgba(47, 230, 115, 0.15)",
                borderRadius: "10px",
                border: "1.8px solid #47e373",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ color: "#b5e3ff", fontWeight: 600 }}>
                🧪 Browser Test Runner (Development Only)
              </div>
              <button
                onClick={() => navigate("/testQuizscript")}
                style={{
                  ...BUTTON_STYLE,
                  padding: "0.5em 1.5em",
                  fontSize: "0.95em",
                  background: "#47e373",
                }}
              >
                Open Test Runner
              </button>
            </div>
          )}

          {error && (
            <div
              style={{
                color: "#fe8a8a",
                marginBottom: 10,
                fontWeight: 800,
                fontSize: "1.08em",
              }}
            >
              {error}
            </div>
          )}
          {success && (
            <div
              style={{
                color: "#7fff9f",
                marginBottom: 10,
                fontWeight: 800,
                fontSize: "1.08em",
              }}
            >
              {success}
            </div>
          )}

          {/* INVITE USER */}
          {isMaster && (
            <form onSubmit={handleInviteUser} style={{ marginBottom: "26px" }}>
              <h4
                style={{
                  marginBottom: 10,
                  color: "#b5e3ff",
                  fontWeight: 700,
                }}
              >
                Invite New User
              </h4>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Enter email address"
                style={{
                  padding: "0.5em 1em",
                  borderRadius: 10,
                  border: "1.8px solid #338",
                  minWidth: 230,
                  fontSize: "1.03em",
                  marginRight: 12,
                }}
                required
              />
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                style={{
                  padding: "0.5em 1.2em",
                  borderRadius: 10,
                  border: "1.8px solid #338",
                  minWidth: 140,
                  fontSize: "1.03em",
                  marginRight: 12,
                }}
                required
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {roleDisplay(r)}
                  </option>
                ))}
              </select>
              <button type="submit" style={BUTTON_STYLE}>
                Invite
              </button>
            </form>
          )}

          {/* USER TABLE */}
          <div
            style={{
              overflowX: "auto",
              background: "rgba(28,34,58,0.13)",
              borderRadius: "1.2em",
              padding: "8px",
              marginBottom: 14,
              boxShadow: "0 4px 16px 0 #2224",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                color: "#fff",
              }}
            >
              <thead>
                <tr style={{ background: "rgba(35,35,61,0.15)" }}>
                  <th style={{ padding: "8px" }}>Email</th>
                  <th style={{ padding: "8px" }}>Name</th>
                  <th style={{ padding: "8px" }}>Role</th>
                  <th style={{ padding: "8px" }}>Created</th>
                  <th style={{ padding: "8px" }}>Last Login</th>
                  <th style={{ padding: "8px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        color: "#fff",
                        fontWeight: 700,
                        textAlign: "center",
                        padding: 14,
                      }}
                    >
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        color: "#b5b8c6",
                        fontWeight: 700,
                        textAlign: "center",
                        padding: 14,
                      }}
                    >
                      No users found.
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.uid || u.email}>
                      <td style={{ padding: "7px 6px" }}>{u.email}</td>
                      <td style={{ padding: "7px 6px" }}>{u.displayName}</td>
                      <td style={{ padding: "7px 6px" }}>
                        {(isMaster || (isCreator && u.role === "Quiz Host")) ? (
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.uid, e.target.value)}
                            style={{
                              padding: "0.4em 1.1em",
                              borderRadius: 9,
                              border: "1.6px solid #338",
                              background: "#1e2b38",
                              color: "#ffe066",
                              fontWeight: 800,
                            }}
                            disabled={u.uid === user?.uid}
                          >
                            {ROLES.map((r) =>
                              isMaster || (isCreator && r === "Quiz Host") ? (
                                <option key={r} value={r}>
                                  {roleDisplay(r)}
                                </option>
                              ) : null
                            )}
                          </select>
                        ) : (
                          <span>{roleDisplay(u.role)}</span>
                        )}
                      </td>
                      <td style={{ padding: "7px 6px" }}>{formatDate(u.createdAt)}</td>
                      <td style={{ padding: "7px 6px" }}>{formatDate(u.lastLogin)}</td>
                      <td style={{ padding: "7px 6px" }}>
                        {(isMaster || (isCreator && u.role === "Quiz Host")) && u.uid !== user?.uid ? (
                          <button style={DANGER_BUTTON} onClick={() => handleDelete(u.uid)}>
                            Delete
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: "0.98em", color: "#ffe066", marginTop: 8 }}>
            {isMaster
              ? "You are a Quiz Master: full permissions."
              : isCreator
              ? "You are a Quiz Creator: you may manage Quiz Hosts only."
              : "You are a Quiz Host: no admin permissions."}
          </div>
        </div>
      )}
    </div>
  );
}
